import { Link } from 'react-router-dom';
import { CheckCircle2, CircleSlash, Trophy } from 'lucide-react';
import { Card, InfoHint } from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatDate, formatMoney, formatMoneySigned, formatPercent } from '../../lib/format';
import type { BonusCondition, CashflowReport } from '../cashflow/api';

/**
 * The bonus programme (Jesse, 2026-08-12): *"we have to have at least a cash
 * level of $240k in the bank, and a quarterly profit that is over $15,000."*
 *
 * Rules this card exists to obey:
 *  - **Both conditions are required**, so both are shown separately with their
 *    own actual against their own threshold. *"'Not met' with no reason is
 *    useless."*
 *  - **Say by how much.** Every failing row prints the shortfall, not a cross.
 *  - **Say which quarter, and whether it is finished.** A quarter two weeks in
 *    that is $4,000 short is not a quarter that missed.
 *  - Thresholds are configurable — the link goes to the plan bar that owns them.
 *
 * Every figure is a field of the one `/api/cashflow` computation: the cash
 * figure is the same `runway.currentCash` the Cash on hand tile prints, and the
 * profit figure is the same quarter total the Report grid colours green.
 *
 * ⚠⚠ **AND THE PROFIT CONDITION IS TESTED ON CASH RECEIVED.**
 * A critic found the exact defect the Revenue-vs-goal tile had just been fixed
 * for, one tile lower and with money attached to it: the row printed
 * *"Profit · Q3 2026 · $112,315 · +$97,315 over"* under a green tick, and
 * **108% of that figure was money nobody had received** — $120,985 of overdue
 * invoices (the oldest 252 days) that the cash report's roll-forward rule moves
 * INTO the current period, plus $3,025 of recurring not yet issued. On cash
 * actually received the same quarter is **−$11,695**, i.e. **$26,695 SHORT** of
 * the $15,000 floor. The roll-forward rule is labelled on every other screen
 * that moves that number; it was labelled nowhere on the one card where the
 * number authorises money leaving the building.
 *
 * So the server decides `met` on cash received (`bonus.profit.actual`), the
 * Report grid's green band follows the same test, and the forecast reading is
 * **kept, printed and reconciled** in `ProfitBasisHint` — behind the (i) on the
 * Profit row since 2026-08-13 — rather than deleted: three lines that add up,
 * instead of one that flatters.
 *
 * ⚠ On a CLOSED quarter the two bases are equal by construction (an elapsed
 * period carries no expected, no recurring-to-be-billed and no projected line),
 * so the reconciliation block only ever appears on a quarter in progress.
 */
export function BonusCard({ report, planHref }: { report: CashflowReport; planHref: string }) {
  const { bonus } = report;
  const currency = report.currency;
  const money = (value: number) => formatMoney(value, { currency, whole: true, parens: true });
  const signed = (value: number) => formatMoneySigned(value, { currency, whole: true });

  const state = bonus.met
    ? { label: 'On track', tone: 'text-status-paid', bg: 'bg-status-paid-bg', ring: 'ring-status-paid/30' }
    : { label: 'Not met', tone: 'text-status-late', bg: 'bg-status-late-bg', ring: 'ring-status-late/30' };
  const failing = [
    ...(bonus.cash.met ? [] : ['cash on hand']),
    ...(bonus.profit.met ? [] : ['quarterly profit']),
  ];

  return (
    <Card padding="none" className="overflow-hidden">
      <div className="flex items-baseline justify-between gap-3 border-b border-grey-100 px-4 py-2.5">
        {/* heading-4, matching every other card title on this screen — see
            BlockHeader's note on the two-ranks-of-title problem. */}
        <h3 className="text-heading-4 text-secondary">Bonus status</h3>
        <span className="truncate text-caption text-grey-400">Both conditions required</span>
      </div>

      <div className={cn('flex items-center gap-3 px-4 py-3', state.bg)}>
        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white ring-1', state.ring)}>
          <Trophy className={cn('h-5 w-5', state.tone)} aria-hidden />
        </span>
        <div className="min-w-0">
          <p className={cn('text-heading-4 leading-none', state.tone)}>{state.label}</p>
          <p className="mt-1 text-caption leading-snug text-grey-600">
            {bonus.met ? (
              <>Both conditions hold on {bonus.profit.label.split(' · ')[0]}.</>
            ) : (
              <>
                Short on {failing.join(' and ')}
                {failing.length === 1 ? '' : ' — both must hold'}.
              </>
            )}
          </p>
        </div>
      </div>

      <ul className="divide-y divide-grey-100">
        <ConditionRow
          label="Cash on hand"
          condition={bonus.cash}
          money={money}
          signed={signed}
          detail={
            bonus.cash.asOf
              ? `${
                  bonus.cash.source === 'period-actual'
                    ? 'Reconciled balance'
                    : bonus.cash.source === 'snapshot'
                      ? 'Latest cash snapshot'
                      : 'Projected (no balance recorded)'
                }, ${formatDate(bonus.cash.asOf)}`
              : 'No balance recorded yet'
          }
        />
        <ConditionRow
          label={`Profit · ${bonus.profit.label}`}
          condition={bonus.profit}
          money={money}
          signed={signed}
          /* ⚠ THE RECONCILIATION LADDER IS BEHIND THE (i), NOT UNDER THE CARD.
             Jesse, 2026-08-13: *"too much explanation in the bonus status"*.
             The verdict, both conditions with actual/threshold/gap, the quarter
             and the two links stay on screen; the four-line arithmetic that
             reconciles cash received to the cash forecast is one click away, on
             the row it explains. */
          info={<ProfitBasisHint bonus={bonus} money={money} />}
          /* ⚠ NAME THE BASIS BEFORE THE PERIOD. "Quarter to date — day 44 of
             92" said which DAYS were counted and never which DOLLARS. It is the
             QUALIFIER — four words a reader needs to not misread the figure —
             so it stays on screen while the lesson moves. */
          detail={
            <>
              <span className="font-semibold text-grey-500">Cash received less cash out</span>
              {' · '}
              {bonus.profit.complete ? (
                <>
                  quarter complete, {formatDate(bonus.profit.start)} to{' '}
                  {formatDate(bonus.profit.end)}
                </>
              ) : bonus.profit.elapsedDays <= 0 ? (
                <>
                  quarter has not started, {formatDate(bonus.profit.start)} to{' '}
                  {formatDate(bonus.profit.end)}
                </>
              ) : (
                <>
                  day {bonus.profit.elapsedDays} of {bonus.profit.totalDays} (
                  {formatPercent(bonus.profit.elapsedDays / bonus.profit.totalDays, { decimals: 0 })}{' '}
                  elapsed)
                </>
              )}
            </>
          }
        />
      </ul>

      {bonus.profit.monthsCounted < bonus.profit.monthsInQuarter && (
        <p className="border-t border-grey-100 px-4 py-2 text-caption leading-snug text-status-partial">
          Your fiscal year starts in month {bonus.fiscalYearStartMonth}, so only{' '}
          {bonus.profit.monthsCounted} of this quarter's {bonus.profit.monthsInQuarter} months fall
          inside {report.year}. Switch year to read the rest.
        </p>
      )}

      {/* `mt-auto` so the links sit on the card's bottom edge however tall the
          row makes it — the shared card-height rule, not a hand-set height. */}
      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-grey-100 px-4 py-2">
        <Link
          to={planHref}
          className="text-caption font-semibold text-accent transition-colors hover:underline"
        >
          Change the thresholds
        </Link>
        {/* THE THIRD READING OF THE SAME QUARTER, NAMED AND LINKED. Cash
            received, the cash forecast and accrual profit are three different
            questions and this app answers all three, so the card that leads
            with one says where the others live. (The Budget tab's closing
            paragraph is the model.) */}
        {/* ⚠ `?basis=accrual` IS LOAD-BEARING. The P&L defaults to CASH now
            (revenue means money that arrived — `pnl/revenue.ts`), so a bare
            link would land on a statement reading the same basis the card
            already prints, under a label promising the third one. */}
        <Link
          to="/reports/pnl?basis=accrual"
          className="text-caption font-semibold text-accent transition-colors hover:underline"
        >
          Accrual reading · Profit &amp; Loss
        </Link>
      </div>
    </Card>
  );
}

/**
 * **THE RECONCILIATION — from the figure that decides the bonus to the figure
 * the Report grid prints.** Three lines that add up, so neither number can be
 * mistaken for the other and neither is hidden.
 *
 * ⚠ **IT LIVES BEHIND THE (i) ON THE PROFIT ROW, NOT UNDER THE CARD.** The
 * CONTENT is binding — the bonus is tested on cash RECEIVED while the Report
 * grid prints the forecast, and a reader who spots the difference must be able
 * to reconcile them — but it is a paragraph of arithmetic about a figure whose
 * qualifier ("Cash received less cash out") is already printed under it. So it
 * moves, in full, one click away. Nothing here is deleted.
 *
 * It renders only when the two bases differ, which can only happen inside a
 * quarter that is still running — see the card's own note.
 */
function ProfitBasisHint({
  bonus,
  money,
}: {
  bonus: CashflowReport['bonus'];
  money: (value: number) => string;
}) {
  const p = bonus.profit;
  const unreceived = p.expectedIncluded + p.unbilledIncluded;
  if (Math.abs(unreceived) < 1) return null;

  return (
    <InfoHint label="How this profit figure is measured" className="ml-0.5" width={320}>
      <p className="leading-snug text-grey-600">
        The cash forecast reads{' '}
        <span className="num font-semibold text-secondary">{money(p.forecast)}</span> for the same
        quarter, because it counts{' '}
        <span className="font-semibold text-status-late">
          {money(unreceived)} nobody has received
        </span>
        :
      </p>
      <dl className="mt-1.5 space-y-1">
        <BasisLine
          term="Cash received less cash out"
          value={money(p.actual)}
          note="tested above"
          strong
        />
        <BasisLine
          term="+ invoiced, not received"
          value={money(p.expectedIncluded)}
          note={
            p.expectedInvoiceCount > 0
              ? `${p.expectedInvoiceCount} overdue${
                  p.expectedOldestDays > 0 ? ` · oldest ${p.expectedOldestDays} days` : ''
                }`
              : undefined
          }
        />
        <BasisLine term="+ not yet invoiced" value={money(p.unbilledIncluded)} />
        <BasisLine
          term="= cash forecast"
          value={money(p.forecast)}
          note="the Report grid's Profit row"
          rule
        />
      </dl>
      {!p.complete && (
        <p className="mt-1.5 text-caption leading-snug text-grey-400">
          Whole quarter on that forecast basis:{' '}
          <span className="num font-semibold text-grey-600">{money(p.projected)}</span>. A bonus is
          paid in cash, so the condition is tested on the received line.
        </p>
      )}
    </InfoHint>
  );
}

function BasisLine({
  term,
  value,
  note,
  strong,
  rule,
}: {
  term: string;
  value: string;
  note?: string;
  strong?: boolean;
  rule?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3 text-caption',
        rule && 'border-t border-grey-200 pt-1',
      )}
    >
      {/* ⚠ NO `truncate` HERE. The note is the REASON — "(7 overdue · oldest
          252 days)" clipped to "(7 overdue · oldest 252 …", which drops the
          unit off the age of the receivable this whole block is about. It
          wraps instead. */}
      <dt
        className={cn(
          'min-w-0 leading-snug',
          strong ? 'font-semibold text-secondary' : 'text-grey-500',
        )}
      >
        {term}
        {note && <span className="ml-1 font-normal text-grey-400">({note})</span>}
      </dt>
      <dd
        className={cn('num shrink-0', strong || rule ? 'font-bold text-secondary' : 'text-grey-600')}
      >
        {value}
      </dd>
    </div>
  );
}

function ConditionRow({
  label,
  condition,
  detail,
  info,
  money,
  signed,
}: {
  label: string;
  condition: BonusCondition;
  detail: React.ReactNode;
  /** The (i) that carries this row's explanation — see `ProfitBasisHint`. */
  info?: React.ReactNode;
  money: (value: number) => string;
  signed: (value: number) => string;
}) {
  return (
    <li className="px-4 py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-center gap-1.5">
          {condition.met ? (
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-status-paid" aria-hidden />
          ) : (
            <CircleSlash className="h-3.5 w-3.5 shrink-0 text-status-late" aria-hidden />
          )}
          <span className="truncate text-body-sm font-semibold text-secondary">{label}</span>
          {info}
        </span>
        {/* A NEGATIVE FIGURE IS NEVER READ AS A POSITIVE ONE. The parens are
            the convention; the colour is the second signal, same rule the
            Report grid's Profit row follows. */}
        <span
          className={cn(
            'num shrink-0 text-body-sm font-bold',
            condition.actual < 0 ? 'text-primary-600' : 'text-secondary',
          )}
        >
          {money(condition.actual)}
        </span>
      </div>
      {/* The whole reason this card exists: the threshold beside the actual,
          and the distance between them with a sign. */}
      <div className="mt-0.5 flex items-baseline justify-between gap-3">
        <span className="truncate text-caption text-grey-500">
          needs {money(condition.threshold)}
        </span>
        <span
          className={cn(
            'num shrink-0 text-caption font-semibold',
            condition.met ? 'text-success' : 'text-status-late',
          )}
        >
          {signed(condition.gap)} {condition.met ? 'over' : 'short'}
        </span>
      </div>
      <p className="mt-1 text-caption leading-snug text-grey-400">{detail}</p>
    </li>
  );
}
